import React from 'react';
import Immutable from 'immutable';
import moment from 'moment';
import DateTime from 'react-datetime';
import { isValidEmail } from '../../../../common/src/util/emailValidation';
import Select from '../../components/reusable/select/Select';
import { passwordRegex } from "../../../../common/src/regexps";

import config from '../../../../server/src/config.js';

/** [Note! This is from newloyalty. Change it?]
 *
 *  DataTypes for user profile field as defined in the backend's UserFieldDataType enum. (And used in the DB)
 *
 *  Never write to any of the fields in the exported object. Consider them immutable!
 *
 *  IMPORTANT!!
 *  This file and the UserFieldDataType enum on the backend MUST be in sync!
 */

const UserFieldDataType = {

    string: {
        id: 1,
        displayName: "string",
        getErrorMessage: () => "Skal være tekst.", // "Must be a string.",
        requiresOptions: false,
        isCompatibleValue: (value, options) => { // "value" must be a string
            return true;
        },
        createInputElement: (props, options) => {
            return <input type="text" {...props} />
        }
    },
    text: {
        id: 2,
        displayName: "text",
        getErrorMessage: () => "Skal være tekst.", // "Must be a text.",
        requiresOptions: false,
        isCompatibleValue: (value, options) => { // "value" must be a text
            return true;
        },
        createInputElement: (props, options) => {
            return <textarea {...props}>{props.value}</textarea>
        }
    },
    int: {
        id: 3,
        displayName: "integer",
        getErrorMessage: () => "Skal være et tal.", // "Must be an integer.",
        requiresOptions: false,
        isCompatibleValue: (value, options) => { // "value" must be a string
            const i = ~~Number(value); // convert string to number and number to integer
            return String(i) === value; // http://stackoverflow.com/questions/10834796/validate-that-a-string-is-a-positive-integer
        },
        createInputElement: (props, options) => { // TODO: Maybe have an onChange (wrapping the given on change) disallowing non-number input?
            return <input type="text" pattern="[0-9]" {...props} />
        }
    },
    float: {
        id: 4,
        displayName: "float",
        getErrorMessage: () => "Skal være et tal.", // "Must be a number.",
        requiresOptions: false,
        isCompatibleValue: (value, options) => { // "value" must be a string
            const i = Number(value.replace(',', '.')); // convert string to number and number to integer
            return !isNaN(i);
        },
        createInputElement: (props, options) => { // TODO: Maybe have an onChange (wrapping the given on change) disallowing non-number input?
            return <input type="text" pattern="([0-9]*[.])?[0-9]" {...props} />
        }
    },
    date: {
        id: 5,
        displayName: "date",
        getErrorMessage: () => "Skal være en gyldig dato i formatet " + config.formats.dateFormat + ".",//"Must be a valid date in the format YYYY-MM-DD.",
        requiresOptions: false,
        isCompatibleValue: (value, options) => { // "value" must be a string
            return moment(value, config.formats.dateFormat, true).isValid(); // http://momentjs.com/docs/
        },
        createInputElement: (props, options) => {
            return createDateInputElement(props, config.formats.dateFormat, false);
        }
    },
    datetime: {
        id: 6,
        displayName: "datetime",
        getErrorMessage: () => "Skal være et gyldigt tidspunkt i formatet " + config.formats.dateTimeFormat + ".", //"Must be a valid date and time in the format YYYY-MM-DD HH:mm:ss.",
        requiresOptions: false,
        isCompatibleValue: (value, options) => { // "value" must be a string
            return moment.utc(value, config.formats.dateTimeFormat, true).tz("Europe/Copenhagen").isValid(); // http://momentjs.com/docs/
        },
        createInputElement: (props, options) => {
            return createDateInputElement(props, config.formats.dateFormat, config.formats.timeFormat);
        }
    },
    enum: {
        id: 7,
        displayName: "selector",
        getErrorMessage: () => "Skal vælge en af værdierne.",//"Must select one of the values.",
        requiresOptions: true,
        isCompatibleValue: (value, options) => { // "value" must be a string
            if (typeof(options) === "undefined") throw new Error("'options' was undefined for the enum data-type!");
            return options.findIndex(o => o.key === value) != -1;
        },
        createInputElement: (props, options) => {
            if (typeof(options) === "undefined") throw new Error("'options' was undefined for the enum data-type!");
            const optionsHtml = options.map(option => <option value={option.key} key={option.key}>{option.value}</option>);
            return (
                <Select {...props}>
                    {props.placeholder === false || props.placeholder === 'false' ? null : <option disabled value="" key="" className="select-placeholder-option">{props.placeholder ? props.placeholder : "Select value"}</option>}
                    {optionsHtml}
                </Select>
            );
        }
    },
    regex: {
        id: 8,
        displayName: "regex",
        getErrorMessage: () => "Ikke i korrekt format.",//"Not in correct format.",
        requiresOptions: true,
        REGEX_OPTION_KEY: "regex",
        isCompatibleValue: (value, options) => { // "value" must be a string
            if (typeof(options) === "undefined") throw new Error("'options' was undefined for the regex data-type!");
            const regexOption = options.find(o => o.key === UserFieldDataType.regex.REGEX_OPTION_KEY);
            if (!regexOption) return false; // if the regex-option is missing, return false (should never happen)
            const regex = "^("+regexOption.value+")$";
            return value.match(new RegExp(regex,"g")) !== null;
        },
        createInputElement: (props, options) => {
            return <input type="text" {...props} />
        }
    },
    options: {
        id: 9,
        displayName: "option buttons",
        getErrorMessage: () => "Skal vælge en af værdierne.",//"Must select one of the values.",
        requiresOptions: true,
        isCompatibleValue: (value, options) => { // "value" must be a string
            if (typeof(options) === "undefined") throw new Error("'options' was undefined for the options data-type!");
            return options.findIndex(o => o.key === value) != -1;
        },
        createInputElement: (props, options) => {
            const name = props.name;
            const value = props.value;
            const onChange = props.onChange;
            delete props.name;
            delete props.value;
            delete props.onChange;
            if (typeof(options) === "undefined") throw new Error("'options' was undefined for the options data-type!");
            const optionsHtml = options.map(option => <span className="option-container" key={option.key}><span className="option-button"><input type="radio" id={name + '|' + option.key} name={name} onChange={(e) => {
                e.target.value = option.key;
                onChange(e);
            }} checked={option.key === value} disabled={option.disabled === true}/></span><span className={"option-title" + (option.disabled === true ? ' disabled' : '')}><label htmlFor={name + '|' + option.key}>{option.value}</label></span></span>);
            return (
                <div {...props}>
                    {optionsHtml}
                </div>
            );
        }
    },

    /** Data types used only at front end */
    password: {
        id: 1000,
        displayName: "password",
        getErrorMessage: () => "Mindst 6 tegn",//"Must be a password.",
        requiresOptions: false,
        isCompatibleValue: (value, options) => { // "value" must be a string
            return new RegExp(passwordRegex).test(value);
        },
        createInputElement: (props, options) => {
            return <input type="password" {...props} />
        }
    },
    email: {
        id: 1001,
        displayName: "email",
        getErrorMessage: () => "Skal være en e-mail adresse.",//"Must be a valid email address.",
        requiresOptions: false,
        isCompatibleValue: (value, options) => { // "value" must contain the @ character
            return isValidEmail(value); //return /.@./.test(value);
        },
        createInputElement: (props, options) => {
            return <input type="email" {...props} />
        }
    },

    /** Get list of all data-types */
    values: (getAll = false) => {
        const values = [];
        for (const datatypeName in UserFieldDataType) {
            if (UserFieldDataType.hasOwnProperty(datatypeName)) {
                const datatype = UserFieldDataType[datatypeName]; // <- "datatypeName" from the for..in loop is a string, not the object itself, so need to lookup corresponding object...
                if (typeof(datatype) !== "function" && (getAll || datatype.id < 1000))  // <- don't include this method for example - only the data-type definitions
                    values.push(datatype);
            }
        }
        return Immutable.List(values);
    },

    /** Get data-type by id */
    getById: (id) => {
        if (typeof(id) === "string") id = parseInt(id);

        for (const datatype of UserFieldDataType.values(true))
            if (datatype.id == id)
                return datatype;

        throw new Error("The datatype-id " + id + " does not correspond to an existing datatype");
    }
};

// Private helper method to create the input-element of date or datetime types (to avoid duplication above)
function createDateInputElement(props, dateFormat, timeFormat) {

    const dateTimeFormat = timeFormat != false ? dateFormat + " " + timeFormat : dateFormat; // "timeFormat = false" means no time, only date.

    // https://github.com/YouCanBookMe/react-datetime
    const inputProps = Object.assign({}, props); // copy of props - these are the props given to the "inputProps"-prop of <DateTimeField> (will be put on the <input> element)
    const directProps = Object.assign({}, props); // copy of props - these are the props directly sat as props on <DateTimeField>

    delete inputProps.onChange; // delete "onChange", "value" from inputProps - keep everything else
    delete inputProps.value;
    delete inputProps.onFocus; // and also delete onFocus and onBlur
    delete inputProps.onBlur;
    delete inputProps.viewMode;
    Object.keys(directProps).forEach(k => {if (k != "onChange" && k != "value" && k != "onBlur" && k != "onFocus" && k != "viewMode") delete directProps[k];}); // delete everything but "onChange", "value", etc

    if (directProps.onChange) { // The parameter given to onChange from the datatime picker is different to how it is for normal input fields (the value instead of the event), so here i try to convert it to the "event" format (only what's necessary), so the same onChange can be used for Dates as for the other types...
        const oldOnChange = directProps.onChange;
        directProps.onChange = (newValue) => {
            if (typeof(newValue) == "object") { // it is a moment object - convert it to a string
                newValue = moment(newValue).format(dateTimeFormat);
            }
            const e = {
                target : {
                    value : newValue,
                    name : props.name
                }
            };
            oldOnChange(e);
        }
    }

    return <DateTime showToday={true} input={true} closeOnSelect={true} locale="da" dateFormat={dateFormat} timeFormat={timeFormat} defaultText="" inputProps={inputProps} {...directProps} />
}

export default UserFieldDataType;


// WEBPACK FOOTER //
// ./src/client/src/utils/enums/UserFieldDataTypeEnum.js